// DOCS:  https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag

/**
 * Prepare item data for Google Analytics events.
 * @param {Object} product - Product information.
 * @param {number} [index=0] - Index of the item.
 * @param {string} [coupon=''] - Coupon code.
 * @param {number} [coupon_discount_price=0] - Discount price with coupon.
 * @param {number} [quantity=1] - Quantity of the item.
 * @returns {Object} - Formatted item data.
 */
function prepareGAItem(product, index = 0, coupon = '', coupon_discount_price = 0, quantity = 1) {
    return {
        item_id:        product.code,
        item_name:      product.title || "Unknown Product",
        affiliation:    '',
        coupon:         coupon,
        discount:       parseFloat(coupon_discount_price) || 0,
        index:          index,
        item_brand:     product.manufacture?.title || '',
        item_category:  product.category?.title || '',
        item_list_id:   "related_products",
        item_list_name: "Related Products",
        location_id:    "",
        price:          parseFloat(product.prices.current_price) || 0,
        quantity:       parseInt(quantity) || 1
    }
}

/**
 * Format GA4 event data before sending.
 * @param {Object} data - Event data.
 * @param {string|null} transactionId - Transaction ID for deduplication.
 * @returns {Object} - Formatted data.
 */
function formatGA4Data(data, transactionId = null) {
    ['value', 'tax', 'shipping'].forEach(key => {
        if (data[key] && typeof data[key] === 'string') {
            data[key] = parseFloat(data[key]);
        }
    });

    if (!Array.isArray(data.items)) {
        data.items = [data.items];
    }

    data.items = data.items.map(item => {
        return {
            ...item,
            price: parseFloat(item.price) || 0,
            discount: parseFloat(item.discount) || 0,
            quantity: parseInt(item.quantity) || 1,
            item_name: item.item_name || "Unknown Product"
        };
    });

    if (transactionId) {
        data.browser_event_id = transactionId;
    }

    return data;
}

/**
 * Handle Google Analytics events.
 * @param {string} eventName - Name of the event.
 * @param {Object} eventData - Event data.
 */
function handleGAEvent(eventName, eventData) {
    console.log(eventName + "::call");
    console.log('Data:', eventData);
    if (typeof dataLayer !== 'undefined') {
        dataLayer.push({ecommerce: null});
        dataLayer.push({
            event: eventName,
            ecommerce: formatGA4Data(eventData)
        });
    } else {
        console.error("dataLayer is not defined. Event not sent to GA4.");
    }
}

// GA Event functions (updated to use formatGA4Data)
window.ga_view_item_list_event = function (products) {
    handleGAEvent("view_item_list", {
        item_list_id: "related_products",
        item_list_name: "Related products",
        items: products.map((product, index) => prepareGAItem(product, index))
    });
};

window.ga_select_item_event = function (product) {
    handleGAEvent("select_item", {
        item_list_id: "related_products",
        item_list_name: "Related Products",
        items: [prepareGAItem(product)]
    });
};

window.ga_view_item_event = function (product) {
    handleGAEvent("view_item", {
        currency: "EUR",
        value: parseFloat(product.prices.current_price) || 0,
        items: [prepareGAItem(product)]
    });
};

window.ga_add_to_cart_event = function (cart, cartItem, quantity) {
    handleGAEvent("add_to_cart", {
        currency: "EUR",
        value: parseFloat(cartItem.product.prices.current_price) || 0,
        items: [prepareGAItem(cartItem.product, 0, cart.coupon ? cart.coupon.code : '', cartItem.coupon_price, quantity)]
    });
};

window.ga_add_to_wishlist_event = function (product) {
    handleGAEvent("add_to_wishlist", {
        currency: "EUR",
        value: parseFloat(product.prices.current_price) || 0,
        items: [prepareGAItem(product)]
    });
};

window.ga_view_cart_event = function (cart) {
    handleGAEvent("view_cart", {
        currency: "EUR",
        value: parseFloat(cart.total_price) || 0,
        items: cart.items.map((item, index) =>
            prepareGAItem(item.product, index, cart.coupon ? cart.coupon.code : '',
                (item.coupon_total_price / item.quantity), item.quantity))
    });
};

window.ga_remove_from_cart_event = function (cart, cartItem, quantity) {
    handleGAEvent("remove_from_cart", {
        currency: "EUR",
        value: parseFloat(cartItem.product.prices.current_price) || 0,
        items: [prepareGAItem(cartItem.product, 0, cart.coupon ? cart.coupon.code : '', cartItem.coupon_price, quantity)]
    });
};

window.ga_begin_checkout_event = function (cart) {
    handleGAEvent("begin_checkout", {
        currency: "EUR",
        value: parseFloat(cart.total_price) || 0,
        coupon: cart.coupon ? cart.coupon.code : '',
        items: cart.items.map((item, index) => prepareGAItem(item.product, index, cart.coupon ? cart.coupon.code : '', (item.coupon_total_price / item.quantity), item.quantity))
    });
};

window.ga_add_shipping_info_event = function (cart) {
    handleGAEvent("add_shipping_info", {
        currency: "EUR",
        value: parseFloat(cart.total_price) || 0,
        coupon: cart.coupon ? cart.coupon.code : '',
        shipping_tier: cart.shipping_method.title || '',
        items: cart.items.map((item, index) =>
            prepareGAItem(item.product, index, cart.coupon ? cart.coupon.code : '',
                (item.coupon_total_price / item.quantity), item.quantity))
    });
};

window.ga_add_payment_info_event = function (cart) {
    handleGAEvent("add_payment_info", {
        currency: "EUR",
        value: parseFloat(cart.total_price) || 0,
        coupon: cart.coupon ? cart.coupon.code : '',
        payment_type: cart.payment_method.title || '',
        items: cart.items.map((item, index) =>
            prepareGAItem(item.product, index, cart.coupon ? cart.coupon.code : '',
                (item.coupon_total_price / item.quantity), item.quantity))
    });
};

window.ga_purchase_event = function (order) {
    handleGAEvent("purchase", {
        transaction_id: order.prefixed_id,
        currency: "EUR",
        value: parseFloat(order.total_price_without_shipping) || 0,
        tax: parseFloat(order.tax_price) || 0,
        shipping: parseFloat(order.cart.shipping_price) || 0,
        coupon: order.cart.coupon ? order.cart.coupon.code : '',
        items: order.cart.items.map((item, index) =>
            prepareGAItem(item.product, index, order.cart.coupon ? order.cart.coupon.code : '', (item.coupon_total_price / item.quantity), item.quantity))
    });
};









